import React from 'react';
import PropTypes from 'prop-types';
import MediaQuery from 'react-responsive';
import renderHTML from 'react-render-html';
import * as breakpoints from '../../data/breakpoints';
import getPlanPrice from '../../utils/getPlanPrice';
import currencyToSymbol from '../../utils/currencyToSymbol';
import Icon from '../Global/Icons/Icon';
import TextWithTooltip from '../Global/Tooltips/TextWithTooltip';
import TooltipWithDropdown from '../Global/Tooltips/TooltipWithDropdown';
import RadioButton from '../Global/Forms/RadioButton';
import RadioSwitch from '../Global/Forms/RadioSwitch';

const SignupTable = ({ signup, values, onChange, onPlanClick }) => {
  let disabledPlanClasses;
  if (`supported_frequency` in signup) {
    signup.plans.map((planType) => {
      disabledPlanClasses = `${disabledPlanClasses} ${planType}-enabled`;
      return disabledPlanClasses;
    });
  }

  const tableCellClass = `fw-medium`;

  const displayPrice = (planType) => {
    if (signup.currency) {
      const currency = currencyToSymbol(signup.currency);
      let frequency = values.billingFrequency;
      // If we only have one supported_frequency x 2 then hardcode the frequency
      if (signup.supported_frequency && signup.supported_frequency.length === 2) {
        frequency = signup.supported_frequency[0];
        frequency = frequency.replace(`ly`, ``);
      }

      const price = getPlanPrice(planType, frequency, signup.currency, signup.org_id);
      return `${currency}${price} / ${frequency}`;
    }
    return renderHTML(`<br />`);
  };

  let radioSwitch;
  if (`supported_frequency` in signup) {
    if (signup.supported_frequency.includes(`month`) && signup.supported_frequency.includes(`year`)) {
      radioSwitch = (
        <RadioSwitch
          onChange={onChange}
          name="billingFrequency"
          leftLabel="Bill monthly"
          leftValue="month"
          leftChecked={values.billingFrequency === `month`}
          rightLabel="Bill yearly"
          rightValue="year"
          rightChecked={values.billingFrequency === `year`}
        />
      );
    }
  }

  const radioTitleClass = `like-h3 m-b-0`;
  const radioSubtitleClass = `like-h4 color-green fw-medium`;

  const basicRadio = (
    <RadioButton name="planType" value="basic" onChange={onChange} onClick={onPlanClick} checked={values.planType === `basic`}>
      <h2 className={radioTitleClass}>Basic</h2>
      <span className={radioSubtitleClass}>Free</span>
    </RadioButton>
  );

  const standardRadio = (
    <RadioButton name="planType" value="standard" onChange={onChange} onClick={onPlanClick} checked={values.planType === `standard`}>
      <h2 className={radioTitleClass}>Standard</h2>
      <span className={radioSubtitleClass}>{displayPrice(`standard`)}</span>
    </RadioButton>
  );

  const premiumRadio = (
    <RadioButton name="planType" value="premium" onChange={onChange} onClick={onPlanClick} checked={values.planType === `premium`}>
      <h2 className={radioTitleClass}>Premium</h2>
      <span className={radioSubtitleClass}>{displayPrice(`premium`)}</span>
    </RadioButton>
  );

  const enterpriseRadio = (
    <RadioButton name="planType" value="enterprise" onChange={onChange} onClick={onPlanClick} checked={values.planType === `enterprise`}>
      <h2 className={radioTitleClass}>Enterprise</h2>
      <span className={radioSubtitleClass}>Tailored quote</span>
    </RadioButton>
  );

  return (
    <div className={`signup-table-container ${disabledPlanClasses}`}>
      <MediaQuery maxWidth={breakpoints.LARGE}>
        <div className="mobile-plan-select-controls m-b-3">
          <div className="text-center m-b-5">
            <div className="d-inline-block">{radioSwitch}</div>
          </div>
          <div className="mobile-plan-select-controls__plans d-flex flex-wrap">
            <div className="mobile-plan-select-controls__plan--basic m-b-3">{basicRadio}</div>
            <div className="mobile-plan-select-controls__plan--standard m-b-3">{standardRadio}</div>
            <div className="mobile-plan-select-controls__plan--premium m-b-3">{premiumRadio}</div>
            <div className="mobile-plan-select-controls__plan--enterprise m-b-3">{enterpriseRadio}</div>
          </div>
        </div>
      </MediaQuery>

      <table className="signup-table table table-no-border table-layout-fixed w-100">
        <thead className="thead thead-minimal">
          <MediaQuery minWidth={breakpoints.LARGE}>
            {(matches) => {
              if (matches) {
                return (
                  <tr>
                    <th>{radioSwitch}</th>
                    <th>{basicRadio}</th>
                    <th>{standardRadio}</th>
                    <th>{premiumRadio}</th>
                    <th>{enterpriseRadio}</th>
                  </tr>
                );
              }
              return (
                <tr>
                  <th />
                  <th><h2 className="signup-table__plan-label like-h4">Basic</h2></th>
                  <th><h2 className="signup-table__plan-label like-h4">Standard</h2></th>
                  <th><h2 className="signup-table__plan-label like-h4">Premium</h2></th>
                  <th><h2 className="signup-table__plan-label like-h4">Enterprise</h2></th>
                </tr>
              );
            }}
          </MediaQuery>
        </thead>

        <tbody className="tbody tbody-minimal">
          <tr>
            <td className={tableCellClass}>
              <TextWithTooltip
                text="All non-exclusive content"
                tooltip={
                  <TooltipWithDropdown tooltipText="All content except for exclusive coaching videos and animations." />
                }
              />
            </td>
            <td><Icon name="tick" fill="grey" /></td>
            <td><Icon name="tick" fill="grey" /></td>
            <td><Icon name="tick" fill="grey" /></td>
            <td><Icon name="tick" fill="grey" /></td>
          </tr>
          <tr>
            <td className={tableCellClass}>All exclusive warm-ups</td>
            <td><Icon name="tick" fill="grey" /></td>
            <td><Icon name="tick" fill="grey" /></td>
            <td><Icon name="tick" fill="grey" /></td>
            <td><Icon name="tick" fill="grey" /></td>
          </tr>
          <tr>
            <td className={tableCellClass}>
              <TextWithTooltip
                text="Team management"
                tooltip={
                  <TooltipWithDropdown tooltipText='Create a single team, add coaches and players and share content with them. <a href="https://www.thecoachingmanual.com/features/club-team-management" target="_blank">Learn more</a>.' />
                }
              />
            </td>
            <td><Icon name="tick" fill="grey" /></td>
            <td><Icon name="tick" fill="grey" /></td>
            <td><Icon name="tick" fill="grey" /></td>
            <td><Icon name="tick" fill="grey" /></td>
          </tr>
          <tr>
            <td className={tableCellClass}>Custom profile</td>
            <td><Icon name="tick" fill="grey" /></td>
            <td><Icon name="tick" fill="grey" /></td>
            <td><Icon name="tick" fill="grey" /></td>
            <td><Icon name="tick" fill="grey" /></td>
          </tr>
          <tr>
            <td className={tableCellClass}>
              <TextWithTooltip
                text="Folders"
                tooltip={
                  <TooltipWithDropdown tooltipText="Organise sessions, practices and content into folders and share with your team." />
                }
              />
            </td>
            <td><Icon name="tick" fill="grey" /></td>
            <td><Icon name="tick" fill="grey" /></td>
            <td><Icon name="tick" fill="grey" /></td>
            <td><Icon name="tick" fill="grey" /></td>
          </tr>
          <tr>
            <td className={tableCellClass}>Ad free</td>
            <td><Icon name="close" fill="grey--mid" /></td>
            <td><Icon name="tick" fill="grey" /></td>
            <td><Icon name="tick" fill="grey" /></td>
            <td><Icon name="tick" fill="grey" /></td>
          </tr>
          <tr>
            <td className={tableCellClass}>
              <TextWithTooltip
                text="All exclusive content"
                tooltip={
                  <TooltipWithDropdown tooltipText='Exclusive professional standard coaching content filmed in broadcast quality. <a href="https://www.thecoachingmanual.com/features/full-coaching-sessions" target="_blank">Learn more</a>.' />
                }
              />
            </td>
            <td><Icon name="close" fill="grey--mid" /></td>
            <td><Icon name="tick" fill="grey" /></td>
            <td><Icon name="tick" fill="grey" /></td>
            <td><Icon name="tick" fill="grey" /></td>
          </tr>
          <tr>
            <td className={tableCellClass}>
              <TextWithTooltip
                text="Season plans"
                tooltip={
                  <TooltipWithDropdown tooltipText='Create a customised Season Plan for your team and players. Save yourself valuable coaching time. <a href="https://www.thecoachingmanual.com/features/season-plans" target="_blank">Learn more</a>.' />
                }
              />
            </td>
            <td><Icon name="close" fill="grey--mid" /></td>
            <td><Icon name="close" fill="grey--mid" /></td>
            <td><Icon name="tick" fill="grey" /></td>
            <td><Icon name="tick" fill="grey" /></td>
          </tr>
          <tr>
            <td className={tableCellClass}>
              <TextWithTooltip
                text="Design tools"
                tooltip={
                  <TooltipWithDropdown tooltipText='Create your own diagrams, practices and sessions with our suite of easy to use tools. <a href="https://thecoachingmanual.com/features/session-planner" target="_blank">Learn more</a>.' />
                }
              />
            </td>
            <td><Icon name="close" fill="grey--mid" /></td>
            <td><Icon name="close" fill="grey--mid" /></td>
            <td><Icon name="tick" fill="grey" /></td>
            <td><Icon name="tick" fill="grey" /></td>
          </tr>
          <tr>
            <td className={tableCellClass}>
              <TextWithTooltip
                text="Club management"
                tooltip={
                  <TooltipWithDropdown tooltipText='Create a club with multiple teams, add coaches and players and share content with them. <a href="https://www.thecoachingmanual.com/features/club-team-management" target="_blank">Learn more</a>.' />
                }
              />
            </td>
            <td><Icon name="close" fill="grey--mid" /></td>
            <td><Icon name="close" fill="grey--mid" /></td>
            <td><Icon name="close" fill="grey--mid" /></td>
            <td><Icon name="tick" fill="grey" /></td>
          </tr>
          <tr>
            <td className={tableCellClass}>
              <TextWithTooltip
                text="Licence management"
                tooltip={
                  <TooltipWithDropdown position="top" tooltipText="Allocate Standard and Premium licences to other members to give them the access they need. Flexibility over the number of licences you need." />
                }
              />
            </td>
            <td><Icon name="close" fill="grey--mid" /></td>
            <td><Icon name="close" fill="grey--mid" /></td>
            <td><Icon name="close" fill="grey--mid" /></td>
            <td><Icon name="tick" fill="grey" /></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

SignupTable.propTypes = {
  signup: PropTypes.object.isRequired,
  values: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onPlanClick: PropTypes.func,
};

export default SignupTable;



// WEBPACK FOOTER //
// ./library/app/components/Signup/SignupTable.js